import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import SenderVerificationAlert from '../../components/SenderVerificationAlert';
import EmailsDetailsForm from '../../components/Emails/EmailsDetailsForm';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import GuidanceModal from '../../components/UserFlows/guidanceModal';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const page = `${Page['email']}`;

const EmailsDetailsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState();
  const [loadError, setLoadError] = useState();
  const { accountId, domainId, emailId } = useParams();
  const [guidanceShow, setGuidanceShow] = useState(false);
  const [useInAutomation, setUseInAutomation] = useState(false);
  const [template, setTemplate] = useState();
  const templateId = queryString.parse(location.search).templateId;

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails/${emailId}`)
      .then(({ email }) => {
        if (!email) {
          navigate(`/accounts/${accountId}/domains/${domainId}/emails`);
        }

        setEmail(email);
      })
      .catch(setLoadError);
  }, [fetch, navigate, accountId, domainId, emailId]);

  const showUserFlow = () => {
    setUseInAutomation(true);
  };

  useEffect(() => {
    if (useInAutomation) {
      setGuidanceShow(true);
    }
  }, [useInAutomation]);

  useEffect(() => {
    if (templateId) {
      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/stripo/templates/${templateId}`)
        .then(({ template }) => {
          setTemplate(template);
        })
        .catch(setLoadError);
    }
  }, [accountId, domainId, fetch, templateId]);

  useEffect(() => {
    if (template?.html && template?.css && email?.design) {
      const newDesign = JSON.stringify({
        html: template.html,
        css: template.css,
      });

      setEmail((prev) => ({ ...prev, design: newDesign }));
    }
  }, [template?.html, template?.css, email?.design]);

  const onDuplicate = () => {
    navigate(`/accounts/${accountId}/domains/${domainId}/emails/new?editor=${email.editor}&from=${email.id}`);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!email) {
    return <Spinner />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/emails` },
    {
      title: email?.name || 'Unknown email',
      href: `#/accounts/${accountId}/domains/${domainId}/emails/${email?.id}/analytics`,
    },
    { title: 'Edit' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | ${email?.name || 'Unknown email'} | Edit`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <SenderVerificationAlert />
      <GuidanceModal
        type="email"
        redirect={`/accounts/${accountId}/domains/${domainId}/emails`}
        show={guidanceShow}
        elementTypes={['campaigns', 'workflows']}
        element={email}
        setShow={() => {
          setGuidanceShow(false);
          setUseInAutomation(false);
        }}
        useInAutomation={useInAutomation}
      />
      <EmailsDetailsForm
        email={email}
        onUpdate={(redirect) => {
          // Update useInAutomation to false when needed
          setUseInAutomation(false);
          setGuidanceShow(false);
          redirect && navigate(`/accounts/${accountId}/domains/${domainId}/emails`);
        }}
        onDelete={() => {
          navigate(`/accounts/${accountId}/domains/${domainId}/emails`);
        }}
        onDuplicate={onDuplicate}
        onBack={() => navigate(-1)}
        showUserFlow={() => {
          showUserFlow();
        }}
      />
    </>
  );
};

export default EmailsDetailsPage;
